import { useContext, useMemo, useState } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { Icon } from "@aidron/aidron-ds";

import { Default } from "../../../components/Default/Default";
import { ProductHierarchyFilter, LoadingContent } from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { SYSTEM_COLORS } from "../../../components/system.colors";

import { TenantContext } from "../../../templates/context/TenantContext";
import { useOtbViewModel } from "../../../viewModel/useOtbVewModel";

import {
  compactCurrencyFormatter,
  createPercentFormatter,
  createDecimalFormatter,
} from "../../../utils/formatter/formatter";

import {
  ALL_KEY,
  DEFAULT_PERIOD,
  PERIOD_OPTIONS,
  buildProductAnalytics,
  buildHierarchyOptions,
  buildGroupOptions,
  chartDivisor,
} from "../../../viewModel/builder/productAnalytics/productAnalytics.builder";

import { CategoryPerformanceTable } from "./components/CategoryPerformanceTable";
import { ClusterPerformanceSection } from "./components/ClusterPerformanceSection";
import { StockHealthSection } from "./components/StockHealthSection";
import { CoverageSection } from "./components/CoverageSection";
import { SectionTitle } from "./components/SectionTitle";
import { FilterPill } from "./components/FilterPill";

const percentFmt = createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const decimalFmt = createDecimalFormatter({ maximumFractionDigits: 1 });
const compactNumberFmt = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

const formatAmount = (value, isItems) =>
  isItems ? `${compactNumberFmt.format(value)} peças` : compactCurrencyFormatter(value);

const TrendArrow = ({ ctx }) => {
  const { up, color } = ctx || {};

  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      {up
        ? <polyline points="18 15 12 9 6 15" />
        : <polyline points="6 9 12 15 18 9" />}
    </svg>
  );
};

const KpiCard = ({ ctx, uiState }) => {

  const {
    label,
    value, 
    delta,
  } = ctx || {}

  const {
    deltaUnit,
    icon,
    inverse
  } = uiState || {}

  const hasDelta = delta !== null && delta !== undefined;
  const isUp = delta > 0;
  const isGood = inverse ? !isUp : isUp;
  const trendColor = isGood ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
      borderRadius: "14px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      padding: "1.2rem 1.4rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.6rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.4px" }}>
          {label}
        </span>
        <Icon icon={icon} size={22} color="color-primary-medium" />
      </div>

      <span style={{ fontSize: "1.7rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, lineHeight: 1.1 }}>
        {value}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", minBlockSize: "1.2rem" }}>
        {hasDelta ? (
          <>
            <TrendArrow ctx={{ up: isUp, color: trendColor }}/>
            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: trendColor }}>
              {`${isUp ? "+" : ""}${decimalFmt.format(delta)}${deltaUnit}`}
            </span>
            <span style={{ fontSize: "0.78rem", color: SYSTEM_COLORS.GRAY.GREY }}>
              vs. ano anterior
            </span>
          </>
        ) : (
          <span style={{ fontSize: "0.78rem", color: SYSTEM_COLORS.GRAY.GREY }}>
            sem dados do ano anterior
          </span>
        )}
      </div>
    </div>
  );
};

const MonthlyMetaPill = ({ ctx }) => {

  const { month, atingimento: fraction } = ctx || {};

  const hasMeta = fraction !== null && fraction !== undefined;
  const atingimento = hasMeta ? fraction * 100 : 0;
  const isOnTarget = atingimento >= 100;
  const isClose = !isOnTarget && atingimento >= 90;

  const background = !hasMeta
    ? SYSTEM_COLORS.WHITE.WHITE_SMOKE
    : isOnTarget
    ? SYSTEM_COLORS.GREEN.FOREST_GREEN_10
    : isClose
      ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE_12
      : SYSTEM_COLORS.RED.BRICK_RED_10;

  const color = !hasMeta
    ? SYSTEM_COLORS.GRAY.GREY
    : isOnTarget
    ? SYSTEM_COLORS.GREEN.FOREST_GREEN
    : isClose
      ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE
      : SYSTEM_COLORS.RED.BRICK_RED;

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "0.35rem 0.8rem",
      borderRadius: "999px", background, color, fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap",
    }}>
      {`${month} · ${hasMeta ? `${Math.round(atingimento)}%` : "—"}`}
    </span>
  );
};

const LegendDot = ({ ctx, uiState }) => {
  const { label } = ctx || {};
  const { color, dashed } = uiState || {};

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
      <svg width={16} height={10} viewBox="0 0 16 10">
        <line x1="0" y1="5" x2="16" y2="5" stroke={color} strokeWidth="2.5" strokeDasharray={dashed ? "3 3" : undefined} />
      </svg>
      {label}
    </span>
  );
};

const ChartTooltip = ({ active, payload, label, isItems, divisor }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
      borderRadius: "10px",
      padding: "0.7rem 0.9rem",
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      fontSize: "0.75rem",
    }}>
      <div style={{ fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, marginBottom: "0.4rem" }}>{label}</div>
      {payload.filter((entry) => entry.value !== null && entry.value !== undefined).map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
          <span>{entry.name}</span>
          <span style={{ fontWeight: 700, color: entry.color }}>
            {["margemPct", "remarcacaoPct"].includes(entry.dataKey)
              ? percentFmt.format(entry.value / 100)
              : formatAmount(entry.value * divisor, isItems)}
          </span>
        </div>
      ))}
    </div>
  );
};

const StatBox = ({ ctx, uiState }) => {
  const { label, value } = ctx || {};
  const { highlight } = uiState || {};

  return (
    <div style={{
      background: highlight ? SYSTEM_COLORS.ORANGE.PEACH : SYSTEM_COLORS.WHITE.WHITE_SMOKE,
      borderRadius: "12px",
      padding: "0.9rem 1.2rem",
      flex: 1,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.68rem", fontWeight: 700, color: highlight ? SYSTEM_COLORS.ORANGE.RUST : SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.4px", marginBottom: "0.3rem" }}>
        {label}
      </div>
      <div style={{ fontSize: "1.3rem", fontWeight: 700, color: highlight ? SYSTEM_COLORS.ORANGE.DEEP_ORANGE : SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
        {value}
      </div>
    </div>
  );
};

export function ProductAnalyticsScreen() {
  const { hierarchy, unit } = useContext(TenantContext);
  const levels = hierarchy?.levels;
  const labels = hierarchy?.labels;

  const isItems = unit === "items";
  const divisor = chartDivisor(unit);
  const realizedLabel = isItems ? "Peças vendidas" : "Venda líquida";

  const vm = useOtbViewModel({ shouldFetchMerchandiseAnalytics: true });

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [group, setGroup] = useState(ALL_KEY);
  const [productHierarchySelection, setProductHierarchySelection] = useState([]);

  const detailsByLevel = useMemo(
    () => buildHierarchyOptions(vm?.ctx?.merchandiseAnalytics, levels ?? []),
    [vm?.ctx?.merchandiseAnalytics, levels]
  );

  const groupOptions = useMemo(
    () => buildGroupOptions(vm?.ctx?.merchandiseAnalytics, levels ?? []),
    [vm?.ctx?.merchandiseAnalytics, levels]
  );

  const analytics = useMemo(
    () => buildProductAnalytics({
      data: vm?.ctx?.merchandiseAnalytics,
      unit,
      levels: levels ?? [],
      period,
      group,
      hierarchySelection: productHierarchySelection,
    }),
    [
      vm?.ctx?.merchandiseAnalytics,
      unit,
      levels,
      period,
      group,
      productHierarchySelection
    ]
  );

  const {
    kpis = [],
    chartData = [],
    generatedAt = "—",
    categories = [],
    categoryTotal = null,
    defaultExpandedId = null,
    clusters = [],
    clusterTotal = null,
    metaSummary = { realizado: 0, meta: null, atingimento: null },
    stockHealth = { buckets: [], total: null },
    coverage,
  } = analytics;

  const leftAxisMax = useMemo(() => {
    const maxValue = Math.max(0, ...chartData.flatMap((item) => [item.realizado, item.meta ?? 0]));
    return Math.max(1, Math.ceil(maxValue));
  }, [chartData]);

  if (vm?.uiState?.isLoadingMerchandiseAnalytics)
    return <LoadingContent label="Carregando análises..." type={LOADING_CONTENT_TYPE.API_PAGE_DATA} />

  return (
    <Default>
      <div style={{ display: "flex", flexDirection: "column", inlineSize: "100%", padding: "2rem 1.5rem 3rem 1rem" }}>

        <SectionTitle
          title="PERFORMANCE DE PRODUTO"
          description={`Última atualização: ${generatedAt}`}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "0.9rem", marginBlockEnd: "1.5rem" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.6px" }}>
            FILTROS
          </span>

          <ProductHierarchyFilter
            ctx={{
              detailsByLevel,
              hierarchy: { levels, labels },
              selectedPaths: productHierarchySelection,
            }}
            uiState={{ label: "Produto", expandOnSelect: false }}
            actions={{ onChange: setProductHierarchySelection }}
          />

          <FilterPill
            variant="active"
            ctx={{ label: "Todas", value: group, options: groupOptions }}
            onChange={setGroup}
          />

          <FilterPill
            variant="active"
            ctx={{ label: "9 meses", value: period, options: PERIOD_OPTIONS }}
            onChange={setPeriod}
          />
        </div>

        {!analytics.hasData && (
          <p style={{ color: SYSTEM_COLORS.GRAY.GREY, fontSize: "0.9rem" }}>
            Nenhum dado disponível no momento
          </p>
        )}

        {analytics.hasData && (
          <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.2rem", marginBlockEnd: "2rem" }}>
          {kpis.map((kpi) => (
            <KpiCard
              key={kpi.label}
              ctx={{ label: kpi.label, value: kpi.value, delta: kpi.delta }}
              uiState={{ deltaUnit: kpi.deltaUnit, icon: kpi.icon, inverse: kpi.inverse }}
            />
          ))}
        </div>

        <SectionTitle
          title="VENDA, MARGEM E REMARCAÇÃO"
          description="Evolução da venda líquida contra a meta (plano/OTB), a margem realizada e o peso da remarcação em cada mês — com o % de atingimento da meta."
        />

        <section style={{
          background: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "1.5rem 2rem",
        }}>
          <div style={{ display: "flex", gap: "1rem", marginBlockEnd: "1.5rem" }}>
            <StatBox ctx={{ label: "META NO PERÍODO", value: metaSummary.meta === null ? "—" : formatAmount(metaSummary.meta, isItems) }} />
            <StatBox ctx={{ label: "REALIZADO", value: formatAmount(metaSummary.realizado, isItems) }} />
            <StatBox ctx={{ label: "ATINGIMENTO DA META", value: metaSummary.atingimento === null ? "—" : percentFmt.format(metaSummary.atingimento) }} uiState={{ highlight: true }} />
          </div>

          <div style={{ blockSize: "22rem" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={SYSTEM_COLORS.GRAY.PLATINUM} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }} />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  domain={[0, leftAxisMax]}
                  tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}
                  tickFormatter={(v) => (isItems ? `${decimalFmt.format(v)} mil` : `R$ ${decimalFmt.format(v)} mi`)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 60]}
                  tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip content={<ChartTooltip isItems={isItems} divisor={divisor} />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <Bar yAxisId="left" dataKey="realizado" name={realizedLabel} fill={SYSTEM_COLORS.ORANGE.DEEP_ORANGE} radius={[3, 3, 0, 0]} maxBarSize={40} />
                <Line yAxisId="left" type="monotone" dataKey="meta" name="Meta" stroke={SYSTEM_COLORS.GRAY.SLATE_GRAY} strokeWidth={2} strokeDasharray="5 3" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="margemPct" name="Margem %" stroke={SYSTEM_COLORS.GREEN.FOREST_GREEN} strokeWidth={2.5} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="remarcacaoPct" name="Remarcação %" stroke={SYSTEM_COLORS.RED.BRICK_RED} strokeWidth={2} strokeDasharray="2 3" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: "1.6rem", marginBlockStart: "1rem" }}>
            <LegendDot ctx={{ label: realizedLabel }} uiState={{ color: SYSTEM_COLORS.ORANGE.DEEP_ORANGE }} />
            <LegendDot ctx={{ label: "Meta" }} uiState={{ color: SYSTEM_COLORS.GRAY.SLATE_GRAY, dashed: true }} />
            <LegendDot ctx={{ label: "Margem %" }} uiState={{ color: SYSTEM_COLORS.GREEN.FOREST_GREEN }} />
            <LegendDot ctx={{ label: "Remarcação %" }} uiState={{ color: SYSTEM_COLORS.RED.BRICK_RED, dashed: true }} />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.5rem", marginBlockStart: "1.2rem" }}>
            {chartData.map((item) => <MonthlyMetaPill key={item.month} ctx={{ month: item.month, atingimento: item.atingimento }} />)}
          </div>
        </section>

        <CategoryPerformanceTable ctx={{ categories, total: categoryTotal, defaultExpandedId }} />

        <ClusterPerformanceSection ctx={{ clusters, total: clusterTotal }} />

        {stockHealth.buckets.length > 0 && <StockHealthSection ctx={stockHealth} />}

        <CoverageSection ctx={coverage} />
          </>
        )}

      </div>
    </Default>
  );
}